/**
 * Business Info Step - Step 2 of Onboarding
 */

import React, { useState } from 'react';
import { useAppSelector } from '../../hooks/useRedux';
import {
  selectCountryOptions,
  selectIsLoadingCountries,
} from '../../store/selectors';
import { BusinessType } from '../../types/company';
import { Button, Input, Select } from '../common';

interface BusinessInfoStepProps {
  initialData?: {
    businessName?: string;
    businessType?: BusinessType;
    countryId?: number;
  };
  onNext: (data: {
    businessName: string;
    businessType: BusinessType;
    countryId: number;
  }) => void;
  onBack: () => void;
  isLoading?: boolean;
}

const BUSINESS_TYPES = [
  { value: BusinessType.RESTAURANT_BAR, label: 'Restaurant / Bar' },
  { value: BusinessType.RETAIL, label: 'Retail Store' },
  { value: BusinessType.OTHER, label: 'Other' },
];

export const BusinessInfoStep: React.FC<BusinessInfoStepProps> = ({
  initialData,
  onNext,
  onBack,
  isLoading = false,
}) => {
  // Get countries from Redux store
  const countryOptions = useAppSelector(selectCountryOptions);
  const isLoadingCountries = useAppSelector(selectIsLoadingCountries);

  const [formData, setFormData] = useState({
    businessName: initialData?.businessName || '',
    businessType: initialData?.businessType || ('' as BusinessType),
    countryId: initialData?.countryId?.toString() || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }

    if (!formData.businessType) {
      newErrors.businessType = 'Please select a business type';
    }

    if (!formData.countryId) {
      newErrors.countryId = 'Please select your country';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onNext({
        businessName: formData.businessName,
        businessType: formData.businessType,
        countryId: parseInt(formData.countryId, 10),
      });
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="mb-8 text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-1 font-sans">
          Tell us about your business
        </h2>
        <p className="text-sm text-gray-500">
          {`We'll set up your first outlet with this information`}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Business Name"
          type="text"
          value={formData.businessName}
          onChange={(val) =>
            setFormData({ ...formData, businessName: val.toString() })
          }
          error={errors.businessName}
          placeholder={`e.g., Joe's Coffee Shop`}
          helperText="This will be used as your first outlet name"
          required
        />

        <Select
          label="Business Type"
          value={formData.businessType}
          onChange={(value) =>
            setFormData({ ...formData, businessType: value as BusinessType })
          }
          options={BUSINESS_TYPES}
          error={errors.businessType}
          placeholder="Select business type"
          required
        />

        <Select
          label="Country"
          value={formData.countryId}
          onChange={(value) => setFormData({ ...formData, countryId: value })}
          options={countryOptions}
          error={errors.countryId}
          placeholder={
            isLoadingCountries ? 'Loading countries...' : 'Select your country'
          }
          required
          disabled={isLoadingCountries}
        />

        <div className="flex gap-4 pt-4">
          <Button type="button" onClick={onBack} variant="outline" fullWidth>
            Back
          </Button>
          <Button type="submit" fullWidth size="lg" isLoading={isLoading}>
            Continue
          </Button>
        </div>
      </form>
    </div>
  );
};
